import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import backArrow from "../../assets/images/backArrow.svg";
import { buildSessionPath, getStoredSession } from "../mmtUserList/mmtFlow";
import {
  addPatient,
  fetchLoggedUserInfo,
  isDuplicateIdError,
  isExternalIdTaken,
} from "../mmtUserList/mmtApi";
import { USER_LIST } from "../../routes/routerPath";
import { isWebView, postWebViewMessage } from "../../utlis/webViewBridge";
import {
  getPersonTerminology,
  resolveCustomerType,
} from "../../utlis/personTerminology";
import { useT } from "../../i18n/useT";
import { getActiveLocale } from "../../configs/regionalProfile";
import { getCurrentLocale } from "../../utlis/locale";
import { isLastNameFirst } from "../../utlis/nameOrder";
import {
  MAX_HEIGHT_CM,
  MAX_HEIGHT_FEET,
  MAX_WEIGHT_KG,
  MAX_WEIGHT_LBS,
  MIN_HEIGHT_CM,
  MIN_HEIGHT_FEET,
  MIN_WEIGHT_KG,
  MIN_WEIGHT_LBS,
  feetAndInchesToCentimeters,
  isHeightCentimetersInRange,
  isWeightKilogramsInRange,
  isWeightPoundsInRange,
  poundsToKilograms,
  usesMetricMeasurements,
} from "./measurementUnits";
import {
  COGNITIVE_CUSTOMER_TYPE,
  COGNITIVE_DIAGNOSES,
  COGNITIVE_MEDICATIONS,
  getDefaultCognitiveBirthYear,
} from "./cognitivePatientFields";
import {
  normalizeExternalId,
  getExternalIdError,
} from "../../utlis/externalId";
import "./testperson.scss";
import DiagnosisDateField from "./DiagnosisDateField";

const ALL_GENDERS = ["male", "female", "other"];
const BIOLOGICAL_GENDERS = ["male", "female"];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 80 }, (_, i) => currentYear - 21 - i);

const isValidName = (s) => /^[\p{L}\p{M}\s'-]+$/u.test(s);

const getCachedAccount = () => {
  try {
    return JSON.parse(sessionStorage.getItem("staffDetails") || "null");
  } catch {
    return null;
  }
};

const getCreatedSeniorUuid = (response) =>
  response?.data?.seniorUuid ||
  response?.data?.uuid ||
  response?.data?.id ||
  response?.result?.seniorUuid ||
  response?.result?.uuid ||
  response?.result?.id ||
  response?.seniorUuid ||
  response?.uuid ||
  response?.id ||
  null;

export default function MmtAddTestPerson({ onClose, onSuccess }) {
  const navigate = useNavigate();
  const t = useT("testPersonForm");
  const tId = useT("externalId");
  const [idTouched, setIdTouched] = useState(false);
  const directWebViewEntry = isWebView() && !onClose && !onSuccess;
  // Units come from the regional profile, never from `locale`.
  const metricMeasurements = usesMetricMeasurements();
  const MONTHS = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: new Date(2000, i, 1).toLocaleString(getActiveLocale(), {
      month: "short",
    }),
  }));
  const [session] = useState(getStoredSession);
  const [account, setAccount] = useState(() =>
    directWebViewEntry ? null : getCachedAccount(),
  );
  const [accountLoading, setAccountLoading] = useState(
    () => directWebViewEntry || !getCachedAccount(),
  );
  const [accountError, setAccountError] = useState("");
  const [step1, setStep1] = useState({
    firstname: "",
    lastname: "",
    testPersonId: "",
    gender: "",
    birthYear: "",
    birthMonth: "",
    cognitiveDiagnosis: "",
    diagnosisDate: "",
  });
  const [step2, setStep2] = useState({
    height: "",
    heightFeet: "",
    heightInches: "",
    weight: "",
    medications: [],
  });
  const [error, setError] = useState("");
  /**
   * External ID duplicate state.
   *
   * Two sources, both real. The preflight queries the existing list endpoint as
   * the user types; the server's rejection of POST /seniors is authoritative
   * and overrides a clean preflight.
   */
  const [duplicateId, setDuplicateId] = useState("");
  const [saving, setSaving] = useState(false);
  const customerType = resolveCustomerType(account);
  const isCognitive = customerType === COGNITIVE_CUSTOMER_TYPE;
  // The iPad close events are part of the cognitive contract only. Any other
  // customer type opened the same way keeps the normal web navigation.
  const nativeCompletion = directWebViewEntry && isCognitive;
  const genders = isCognitive ? BIOLOGICAL_GENDERS : ALL_GENDERS;
  const personLabels = getPersonTerminology(account);

  const setField = (key, value) =>
    setStep1((prev) => {
      const next = { ...prev, [key]: value };
      if (!next.cognitiveDiagnosis) next.diagnosisDate = "";
      return next;
    });
  const setMeasurement = (key, value) =>
    setStep2((prev) => ({ ...prev, [key]: value }));
  const toggleMedication = (value) =>
    setStep2((prev) => ({
      ...prev,
      medications: prev.medications.includes(value)
        ? prev.medications.filter((medication) => medication !== value)
        : [...prev.medications, value],
    }));

  useEffect(() => {
    if (!session) {
      setAccountLoading(false);
      setAccountError(t("invalid_session"));
      return undefined;
    }

    let cancelled = false;
    fetchLoggedUserInfo(session)
      .then((details) => {
        if (cancelled) return;
        setAccount((current) =>
          JSON.stringify(current) === JSON.stringify(details)
            ? current
            : details,
        );
        sessionStorage.setItem("staffDetails", JSON.stringify(details));
        setAccountError("");
      })
      .catch(() => {
        if (!cancelled && (directWebViewEntry || !getCachedAccount())) {
          setAccountError(t("account_load_error"));
        }
      })
      .finally(() => {
        if (!cancelled) setAccountLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [directWebViewEntry, session, t]);

  useEffect(() => {
    if (!isCognitive) return;
    setStep1((prev) => ({
      ...prev,
      gender: prev.gender === "other" ? "" : prev.gender,
      birthYear: prev.birthYear || getDefaultCognitiveBirthYear(),
    }));
  }, [isCognitive]);

  const firstNameError =
    step1.firstname.trim() !== "" && !isValidName(step1.firstname);
  const lastNameError =
    step1.lastname.trim() !== "" && !isValidName(step1.lastname);
  const normalizedId = normalizeExternalId(step1.testPersonId);
  const testIdReason = getExternalIdError(step1.testPersonId);
  const testIdError = Boolean(testIdReason);
  const showIdError = testIdError && (idTouched || step1.testPersonId !== "");
  const metricHeightError =
    step2.height !== "" && !isHeightCentimetersInRange(step2.height);
  const imperialHeightComplete =
    step2.heightFeet !== "" && step2.heightInches !== "";
  const imperialHeightCm = imperialHeightComplete
    ? feetAndInchesToCentimeters(step2.heightFeet, step2.heightInches)
    : null;
  const imperialHeightError =
    imperialHeightComplete && !isHeightCentimetersInRange(imperialHeightCm);
  const heightError = metricMeasurements
    ? metricHeightError
    : imperialHeightError;
  const weightError =
    step2.weight !== "" &&
    !(metricMeasurements
      ? isWeightKilogramsInRange(step2.weight)
      : isWeightPoundsInRange(step2.weight));

  // Preflight the External ID against records this account can already see.
  useEffect(() => {
    const value = normalizedId;
    if (!session || value === "" || testIdError) {
      setDuplicateId("");
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      isExternalIdTaken(session, value).then((taken) => {
        if (!cancelled) setDuplicateId(taken ? value : "");
      });
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [session, normalizedId, testIdError]);

  const isDuplicate = duplicateId !== "" && duplicateId === normalizedId;

  const isFormValid =
    step1.firstname.trim() !== "" &&
    !firstNameError &&
    step1.lastname.trim() !== "" &&
    !lastNameError &&
    normalizedId !== "" &&
    !testIdError &&
    !isDuplicate &&
    step1.gender !== "" &&
    step1.birthYear !== "" &&
    step1.birthMonth !== "" &&
    (!isCognitive || step1.cognitiveDiagnosis !== "") &&
    (metricMeasurements ? step2.height !== "" : imperialHeightComplete) &&
    !heightError &&
    step2.weight !== "" &&
    !weightError;

  const back = () => {
    if (nativeCompletion) {
      postWebViewMessage("ADD_TEST_PERSON_CANCELLED");
      return;
    }
    if (onClose) {
      onClose();
      return;
    }
    if (session) {
      navigate(buildSessionPath(USER_LIST, session), { replace: true });
    } else {
      navigate("/login", { replace: true });
    }
  };

  const done = (response) => {
    sessionStorage.removeItem("userList");
    if (nativeCompletion) {
      postWebViewMessage("TEST_PERSON_CREATED", {
        externalId: normalizedId,
        seniorUuid: getCreatedSeniorUuid(response),
      });
      return;
    }
    if (onSuccess) {
      onSuccess();
      return;
    }
    navigate(buildSessionPath(USER_LIST, session), { replace: true });
  };

  const submit = async () => {
    if (!isFormValid || !session) return;
    setSaving(true);
    setError("");
    try {
      const measurements = metricMeasurements
        ? step2
        : {
            ...step2,
            height: feetAndInchesToCentimeters(
              step2.heightFeet,
              step2.heightInches,
            ),
            weight: poundsToKilograms(step2.weight),
          };
      const response = await addPatient(
        session,
        { ...step1, testPersonId: normalizedId },
        measurements,
      );
      done(response);
    } catch (err) {
      if (isDuplicateIdError(err)) {
        // Authoritative: someone created this ID since the preflight ran.
        setDuplicateId(normalizedId);
        setError(t("id_exists", { person: personLabels.singular }));
        return;
      }
      setError(
        err?.response?.data?.message ||
          err?.message ||
          t("add_error", { person: personLabels.singularLower }),
      );
    } finally {
      setSaving(false);
    }
  };

  const firstNameField = (
    <div className="mmt-field" key="firstname">
      <span>{t("first_name")} *</span>
      <input
        value={step1.firstname}
        onChange={(e) => setField("firstname", e.target.value)}
        maxLength={50}
        className={firstNameError ? "mmt-input-error" : ""}
        disabled={saving}
      />
      {firstNameError ? (
        <em className="mmt-tp-hint">{t("name_hint")}</em>
      ) : null}
    </div>
  );
  const lastNameField = (
    <div className="mmt-field" key="lastname">
      <span>{t("last_name")} *</span>
      <input
        value={step1.lastname}
        onChange={(e) => setField("lastname", e.target.value)}
        maxLength={50}
        className={lastNameError ? "mmt-input-error" : ""}
        disabled={saving}
      />
      {lastNameError ? <em className="mmt-tp-hint">{t("name_hint")}</em> : null}
    </div>
  );
  // Entry order follows display order (`formatName`): family name first in ja/zh.
  const nameFields = isLastNameFirst(getCurrentLocale())
    ? [lastNameField, firstNameField]
    : [firstNameField, lastNameField];

  return (
    <main className="mmt-test-person-page">
      <header className="mmt-tp-header">
        <button onClick={back} aria-label={t("back")} className="mmt-tp-back">
          <img src={backArrow} alt="" />
        </button>
        <h1>{t("add_person", { person: personLabels.singular })}</h1>
      </header>

      <section className="mmt-tp-form">
        {error ? <div className="mmt-tp-error">{error}</div> : null}
        {accountError ? (
          <div className="mmt-tp-error">{accountError}</div>
        ) : null}

        {accountLoading ? <p>{t("loading")}</p> : null}

        {!accountLoading && !accountError ? (
          <>
            {nameFields}

            <div className="mmt-field">
              <span>{t("person_id", { person: personLabels.singular })} *</span>
              <input
                value={step1.testPersonId}
                onChange={(e) => setField("testPersonId", e.target.value)}
                placeholder={tId("placeholder")}
                onBlur={() => setIdTouched(true)}
                aria-label={t("person_id", { person: personLabels.singular })}
                aria-describedby="external-id-helper external-id-error"
                aria-invalid={showIdError || isDuplicate}
                autoCapitalize="none"
                className={showIdError ? "mmt-input-error" : ""}
                disabled={saving}
              />
              <small id="external-id-helper">{tId("helper")}</small>
              {showIdError ? (
                <em id="external-id-error" className="mmt-tp-hint">
                  {tId(testIdReason, { person: personLabels.singular })}
                </em>
              ) : null}
              {!testIdError && isDuplicate ? (
                <em
                  className="mmt-tp-hint"
                  id="external-id-error"
                  data-error="duplicate-id"
                >
                  {t("id_exists", { person: personLabels.singular })}
                </em>
              ) : null}
            </div>

            <div className="mmt-field">
              <span>{t(isCognitive ? "biological_gender" : "gender")} *</span>
              <div className="mmt-tp-seg">
                {genders.map((g) => (
                  <button
                    key={g}
                    type="button"
                    className={`mmt-tp-seg-btn${step1.gender === g ? " selected" : ""}`}
                    onClick={() => setField("gender", g)}
                    disabled={saving}
                  >
                    {t(g)}
                  </button>
                ))}
              </div>
            </div>

            <div className="mmt-tp-row">
              <label className="mmt-field">
                <span>{t("birth_year")} *</span>
                <select
                  value={step1.birthYear}
                  onChange={(e) =>
                    setField(
                      "birthYear",
                      e.target.value ? Number(e.target.value) : "",
                    )
                  }
                  disabled={saving}
                >
                  <option value="">{t("year")}</option>
                  {YEARS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </label>

              <label className="mmt-field">
                <span>{t("birth_month")} *</span>
                <select
                  value={step1.birthMonth}
                  onChange={(e) =>
                    setField(
                      "birthMonth",
                      e.target.value ? Number(e.target.value) : "",
                    )
                  }
                  disabled={saving}
                >
                  <option value="">{t("month")}</option>
                  {MONTHS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="mmt-tp-row">
              <div className="mmt-field">
                <span>
                  {t("height", {
                    unit: metricMeasurements ? t("unit_cm") : t("unit_ft_in"),
                  })}{" "}
                  *
                </span>
                {metricMeasurements ? (
                  <input
                    type="number"
                    min={MIN_HEIGHT_CM}
                    max={MAX_HEIGHT_CM}
                    step="0.1"
                    value={step2.height}
                    onChange={(e) => setMeasurement("height", e.target.value)}
                    className={heightError ? "mmt-input-error" : ""}
                    disabled={saving}
                  />
                ) : (
                  <div className="mmt-height-imperial">
                    <label>
                      <input
                        type="number"
                        min={MIN_HEIGHT_FEET}
                        max={MAX_HEIGHT_FEET}
                        step="1"
                        value={step2.heightFeet}
                        onChange={(e) =>
                          setMeasurement("heightFeet", e.target.value)
                        }
                        className={heightError ? "mmt-input-error" : ""}
                        disabled={saving}
                        aria-label={t("height_feet_aria")}
                      />
                      <span>{t("unit_ft")}</span>
                    </label>
                    <label>
                      <input
                        type="number"
                        min="0"
                        max="11"
                        step="1"
                        value={step2.heightInches}
                        onChange={(e) =>
                          setMeasurement("heightInches", e.target.value)
                        }
                        className={heightError ? "mmt-input-error" : ""}
                        disabled={saving}
                        aria-label={t("height_inches_aria")}
                      />
                      <span>{t("unit_in")}</span>
                    </label>
                  </div>
                )}
                {heightError ? (
                  <em className="mmt-tp-hint">
                    {metricMeasurements
                      ? t("height_hint_metric")
                      : t("height_hint_imperial")}
                  </em>
                ) : null}
              </div>

              <div className="mmt-field">
                <span>
                  {t("weight", {
                    unit: metricMeasurements ? t("unit_kg") : t("unit_lbs"),
                  })}{" "}
                  *
                </span>
                <input
                  type="number"
                  min={metricMeasurements ? MIN_WEIGHT_KG : MIN_WEIGHT_LBS}
                  max={metricMeasurements ? MAX_WEIGHT_KG : MAX_WEIGHT_LBS}
                  step="0.1"
                  value={step2.weight}
                  onChange={(e) => setMeasurement("weight", e.target.value)}
                  className={weightError ? "mmt-input-error" : ""}
                  disabled={saving}
                />
                {weightError ? (
                  <em className="mmt-tp-hint">
                    {t("weight_hint", {
                      min: metricMeasurements ? MIN_WEIGHT_KG : MIN_WEIGHT_LBS,
                      max: metricMeasurements ? MAX_WEIGHT_KG : MAX_WEIGHT_LBS,
                      unit: metricMeasurements ? t("unit_kg") : t("unit_lbs"),
                    })}
                  </em>
                ) : null}
              </div>
            </div>

            {isCognitive ? (
              <div className="mmt-tp-section">
                <label className="mmt-field">
                  <span>{t("cognitive_diagnosis")} *</span>
                  <select
                    value={step1.cognitiveDiagnosis}
                    onChange={(e) =>
                      setField("cognitiveDiagnosis", e.target.value)
                    }
                    disabled={saving}
                  >
                    <option value="">{t("select_diagnosis")}</option>
                    {COGNITIVE_DIAGNOSES.map((diagnosis) => (
                      <option key={diagnosis} value={diagnosis}>
                        {t(`diagnosis_${diagnosis}`)}
                      </option>
                    ))}
                  </select>
                </label>
                <DiagnosisDateField
                  value={step1.diagnosisDate}
                  onChange={(value) => setField("diagnosisDate", value)}
                  disabled={saving || !step1.cognitiveDiagnosis}
                />
              </div>
            ) : null}

            {isCognitive ? (
              <div className="mmt-tp-section">
                <div className="mmt-tp-section-label">{t("medications")}</div>
                <div className="mmt-tp-chip-group">
                  {COGNITIVE_MEDICATIONS.map((medication) => (
                    <button
                      key={medication}
                      type="button"
                      className={`mmt-tp-chip${
                        step2.medications.includes(medication)
                          ? " selected"
                          : ""
                      }`}
                      onClick={() => toggleMedication(medication)}
                      disabled={saving}
                    >
                      {t(`med_${medication}`)}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="mmt-tp-actions">
              <button
                type="button"
                className="mmt-primary-button"
                onClick={submit}
                disabled={!isFormValid || saving}
              >
                {saving
                  ? t("saving")
                  : t("add_person", { person: personLabels.singular })}
              </button>
            </div>
          </>
        ) : null}
      </section>
    </main>
  );
}
